/**
 * UpNextCountdown — Netflix-style "Next episode in Xs" banner.
 *
 * v1.4.62 — Renders a compact card at the bottom of the video player when
 *           the user is watching a series episode AND the queue has a next
 *           episode AND the remaining runtime <= COUNTDOWN_SECONDS. Two
 *           actions:
 *             • "Play now"  → dismiss the countdown and hand the next episode
 *                             back to the parent, which navigates immediately.
 *             • "Cancel"    → dismiss and stay on the current episode until
 *                             it ends. Never re-arms for this playback.
 *
 * Parent is expected to:
 *   1. Only mount this component while a series episode is playing AND a
 *      next episode exists (i.e. `nextEpisode` is truthy).
 *   2. Pass the live `remainingSeconds` — this component's job is purely UI.
 *   3. Handle `onPlayNext()` (navigate to next episode) and `onCancel()`
 *      (dismiss for the rest of this playback).
 */
import React from "react";
import { PLAYER } from "../lib/testIds";
import { PlaySquare, X } from "lucide-react";

export const COUNTDOWN_SECONDS = 10;

export default function UpNextCountdown({
  nextEpisode,
  remainingSeconds,
  onPlayNext,
  onCancel,
}) {
  if (!nextEpisode) return null;
  // Clamp so the badge never renders "11s" or "-1s".
  const secondsShown = Math.max(0, Math.min(COUNTDOWN_SECONDS, Math.ceil(remainingSeconds)));
  const pct = Math.max(0, Math.min(100, (secondsShown / COUNTDOWN_SECONDS) * 100));

  const label = nextEpisode.name || nextEpisode.title || "Next episode";
  const subLabel = formatEpisodeSubLabel(nextEpisode);
  const poster = nextEpisode.logo_url || nextEpisode.poster_url || null;

  return (
    <div
      data-testid={PLAYER.upNextBanner}
      className="absolute bottom-4 right-4 z-30 w-[min(92vw,420px)] rounded-2xl border border-white/10 bg-black/80 backdrop-blur-lg shadow-2xl overflow-hidden"
    >
      {/* Countdown progress rail across the top of the card */}
      <div className="h-1 bg-white/10">
        <div
          className="h-full bg-orange-400 transition-[width] duration-1000 ease-linear"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-center gap-3 p-3">
        {/* Poster */}
        {poster ? (
          <img
            src={poster}
            alt=""
            className="w-16 h-20 rounded-md object-cover shrink-0 bg-black/40"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
          />
        ) : (
          <div className="w-16 h-20 rounded-md bg-white/5 shrink-0 flex items-center justify-center">
            <PlaySquare className="w-6 h-6 text-white/40" />
          </div>
        )}

        {/* Copy */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-orange-400 font-semibold">
            Next episode in{" "}
            <span data-testid={PLAYER.upNextCountdown}>{secondsShown}s</span>
          </div>
          <div className="text-sm font-semibold text-white truncate mt-0.5" title={label}>
            {label}
          </div>
          {subLabel ? (
            <div className="text-[11px] text-slate-400 truncate">{subLabel}</div>
          ) : null}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex border-t border-white/5">
        <button
          type="button"
          data-testid={PLAYER.upNextCancel}
          onClick={onCancel}
          className="flex-1 flex items-center justify-center gap-1.5 text-slate-300 hover:bg-white/5 py-2.5 text-sm transition-colors"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        <div className="w-px bg-white/5" />
        <button
          type="button"
          data-testid={PLAYER.upNextPlayNow}
          onClick={onPlayNext}
          autoFocus
          className="flex-1 flex items-center justify-center gap-1.5 text-white bg-orange-500 hover:bg-orange-600 py-2.5 text-sm font-semibold transition-colors"
        >
          <PlaySquare className="w-4 h-4" />
          Play now
        </button>
      </div>
    </div>
  );
}

function formatEpisodeSubLabel(ep) {
  if (!ep) return "";
  const s = ep.season ?? ep.season_num ?? null;
  const e = ep.episode ?? ep.episode_num ?? null;
  if (s != null && e != null) return `Season ${s} · Episode ${e}`;
  if (e != null) return `Episode ${e}`;
  return ep.series_name || "";
}
